import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import type { FileRejection } from 'react-dropzone';
import { convertGpxToKml, createObjectUrl, InvalidGpxError, KmlStyleOptions, DEFAULT_KML_STYLE_OPTIONS } from '../utils/converters';
import { MAX_GPX_FILE_SIZE_BYTES } from '../components/core/FileDropzone';

const MAX_GPX_FILE_SIZE_MB = MAX_GPX_FILE_SIZE_BYTES / (1024 * 1024);

export function useFileConversion() {
  const [loading, setLoading] = useState(false);
  const [isLoadingExiting, setIsLoadingExiting] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | undefined>();
  const [showDownload, setShowDownload] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [outputFilename, setOutputFilename] = useState("converted.kml");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const toast = useToast();

  function toastError(title: string) {
    toast({
      title,
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top',
      variant: 'subtle',
    });
  }

  function fillObjectUrl(kml: string) {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    setObjectUrl(createObjectUrl(kml));
  }

  async function handleFileDrop(acceptedFiles: File[]) {
    setShowDownload(false);
    setShowSuccessAnimation(false);

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      const originalFilename = file.name;
      const newFilename = originalFilename.replace(/\.gpx$/i, '.kml');
      setOutputFilename(newFilename);
    }
  }

  function handleFileRejected(rejections: FileRejection[]) {
    const code = rejections[0]?.errors[0]?.code;
    if (code === 'file-too-large') {
      toastError(`That file is larger than ${MAX_GPX_FILE_SIZE_MB} MB. Please upload a smaller GPX file.`);
    } else if (code === 'file-invalid-type') {
      toastError('Only .gpx files are supported.');
    } else if (code === 'too-many-files') {
      toastError('Please drop one file at a time.');
    } else {
      toastError('That file could not be accepted.');
    }
  }

  async function convertFile(options: KmlStyleOptions = DEFAULT_KML_STYLE_OPTIONS) {
    if (!selectedFile) {
      toastError("No file selected");
      return;
    }

    setLoading(true);
    setShowSuccessAnimation(false);

    try {
      const minLoadingTime = new Promise(resolve => setTimeout(resolve, 4000));

      let gpxString = await selectedFile.text();
      let kmlString = convertGpxToKml(gpxString, options);
      fillObjectUrl(kmlString);

      await minLoadingTime;

      setIsLoadingExiting(true);
      setTimeout(() => {
        setLoading(false);
        setIsLoadingExiting(false);
        setShowDownload(true);
        setShowSuccessAnimation(true);
      }, 600);
    } catch (e) {
      Sentry.captureException(e);
      const message = e instanceof InvalidGpxError
        ? e.message
        : 'Error converting GPX file. Please check your file and try again.';
      toastError(message);
      setIsLoadingExiting(true);
      setTimeout(() => {
        setLoading(false);
        setIsLoadingExiting(false);
      }, 600);
    }
  }

  return {
    loading,
    isLoadingExiting,
    objectUrl,
    showDownload,
    showSuccessAnimation,
    outputFilename,
    selectedFile,
    handleFileDrop,
    handleFileRejected,
    convertFile,
  };
}
